import type { CSSProperties } from 'react';
import { useEffect, useState } from 'react';

import Sidebar from '../components/Sidebar';
import { api, type Payment } from '../api/client';
import { T } from '../styles/tokens';

const statusMeta: Record<Payment['status'], { label: string; color: string }> = {
  paid: { label: 'Оплачен', color: T.success },
  pending: { label: 'Ожидает', color: T.warn },
  failed: { label: 'Ошибка', color: T.error },
  trial: { label: 'Триал', color: T.accent },
};

export default function Payments() {
  const [rows, setRows] = useState<Payment[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.payments.list().then(setRows).catch(console.error).finally(() => setLoading(false));
  }, []);

  return (
    <div style={pm.root}>
      <Sidebar active="payments" />
      <main style={pm.main}>
        <div style={pm.topBar}>
          <div>
            <div style={pm.pageTag}>// финансы</div>
            <div style={pm.pageTitle}>История платежей</div>
          </div>
        </div>

        <div style={pm.table}>
          <div style={pm.thead}>
            {['Дата', 'Тариф', 'Протокол', 'Сумма', 'Статус', 'Order ID'].map((h) => (
              <div key={h} style={pm.th}>{h}</div>
            ))}
          </div>
          {loading && <div style={{ padding: 20, color: T.textMuted }}>Загрузка...</div>}
          {!loading && rows.length === 0 && (
            <div style={{ padding: 30, color: T.textMuted, textAlign: 'center' }}>Платежей пока нет</div>
          )}
          {rows.map((p) => {
            const meta = statusMeta[p.status];
            return (
              <div key={p.id} style={pm.tr}>
                <div style={pm.td}>{new Date(p.created_at).toLocaleDateString('ru')}</div>
                <div style={pm.td}>{p.plan_label}</div>
                <div style={pm.td}>{p.protocol === 'wireguard' ? 'WireGuard' : p.protocol === 'vless' ? 'VLESS' : '—'}</div>
                <div style={{ ...pm.td, fontFamily: 'DM Mono, monospace' }}>{p.amount} ₽</div>
                <div style={pm.td}>
                  <span style={{ ...pm.badge, color: meta.color, borderColor: meta.color + '55' }}>{meta.label}</span>
                </div>
                <div style={{ ...pm.td, fontFamily: 'DM Mono, monospace', fontSize: 11, color: T.textMuted }}>{p.order_id}</div>
              </div>
            );
          })}
        </div>
      </main>
    </div>
  );
}

const pm: Record<string, CSSProperties> = {
  root: { display: 'flex', minHeight: '100vh', background: T.bg, fontFamily: 'DM Sans, sans-serif', color: T.text },
  main: { flex: 1, marginLeft: 220, padding: '36px 44px', boxSizing: 'border-box', maxWidth: 'calc(100vw - 220px)' },
  topBar: { marginBottom: 32 },
  pageTag: { fontFamily: 'DM Mono, monospace', fontSize: 10, color: T.accent, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 6 },
  pageTitle: { fontFamily: 'Syne, sans-serif', fontWeight: 800, fontSize: 28, letterSpacing: '-0.03em', color: T.text },
  table: { border: `1px solid ${T.border}`, borderRadius: 4, overflow: 'hidden', background: T.surface },
  thead: { display: 'grid', gridTemplateColumns: '110px 1fr 140px 100px 120px 130px', background: T.surfaceUp, borderBottom: `1px solid ${T.border}`, padding: '0 18px' },
  th: { fontFamily: 'DM Mono, monospace', fontSize: 9, color: T.textMuted, textTransform: 'uppercase', letterSpacing: '0.08em', padding: '11px 0' },
  tr: { display: 'grid', gridTemplateColumns: '110px 1fr 140px 100px 120px 130px', padding: '15px 18px', borderBottom: `1px solid ${T.border}`, alignItems: 'center' },
  td: { fontSize: 13, color: T.textSec },
  badge: { fontFamily: 'DM Mono, monospace', fontSize: 10, fontWeight: 600, padding: '3px 8px', border: '1px solid', borderRadius: 2, textTransform: 'uppercase', letterSpacing: '0.08em' },
};
